import { getGenericPermissionError } from '../comments-errors';
import { isOfType, unreachable } from '../../../../ts-utils';
import { ensureMemberHasRightTo } from '../../members/members-actions';
import { canHaveVotes } from '../top-level-comments/top-level-comment-types';
import { getNextCurrentUserVoteState } from './comments-vote-utils';
const COMMENTS_FQDN = 'wix.comments.comment';
export const VOTE_ON_COMMENT_REQUEST = 'VOTE_ON_COMMENT_REQUEST';
export const VOTE_ON_COMMENT_FAILURE = 'VOTE_ON_COMMENT_FAILURE';
export const VOTE_ON_COMMENT_SUCCESS = 'VOTE_ON_COMMENT_SUCCESS';
export const voteOnCommentRequest = (payload) => ({
    type: VOTE_ON_COMMENT_REQUEST,
    payload,
});
export const voteOnCommentFailure = (payload) => ({
    type: VOTE_ON_COMMENT_FAILURE,
    payload,
});
export const voteOnCommentSuccess = (payload) => ({
    type: VOTE_ON_COMMENT_SUCCESS,
    payload,
});
export const voteOnComment = ({ subaction, resourceId, commentId, ctxFields, }) => async (dispatch, getState, { votesService, appDefinitionId }) => {
    const commentState = getCommentStateWithVotes(getState(), resourceId, commentId);
    if (!commentState) {
        return;
    }
    const currentUserVote = commentState.votes.currentUserVote;
    if (currentUserVote.pendingState) {
        return;
    }
    const nextCurrentUserVote = getNextCurrentUserVoteState(currentUserVote, subaction);
    if (nextCurrentUserVote.type === 'INVALID') {
        return;
    }
    dispatch(voteOnCommentRequest({
        resourceId,
        commentId,
        nextCurrentUserVoteState: nextCurrentUserVote,
    }));
    const permissionResult = await dispatch(ensureMemberHasRightTo({
        userAction: 'VOTE',
        resourceId,
        commentId,
    }));
    if (permissionResult !== 'OK') {
        return dispatch(voteOnCommentFailure({
            resourceId,
            commentId,
            error: permissionResult === 'ACTION_CANCELLED'
                ? { type: 'SILENT' }
                : getGenericPermissionError(),
            nextCurrentUserVoteState: currentUserVote,
        }));
    }
    // After permission lift we can have comment state with changed vote state
    const freshCommentState = getCommentStateWithVotes(getState(), resourceId, commentId);
    if (!freshCommentState) {
        return;
    }
    const freshCurrentUserVote = freshCommentState.votes.currentUserVote;
    if (!freshCurrentUserVote.pendingState) {
        // the fresh comment votes are already in desired state
        return;
    }
    const finalCurrentUserVoteState = await (() => {
        switch (freshCurrentUserVote.pendingState) {
            case 'UPVOTE': {
                return votesService
                    .upvote({
                    contextData: {
                        contextId: ctxFields.contextId,
                        contextType: ctxFields.contextType,
                        appDefId: appDefinitionId,
                    },
                    resourceId: commentId,
                    resourceFqdn: COMMENTS_FQDN,
                })
                    .then((result) => result.type === 'SUCCESS'
                    ? { type: 'UPVOTE', voteId: result.response.voteId }
                    : { type: 'FAIL', error: result.error });
            }
            case 'DOWNVOTE': {
                return votesService
                    .downvote({
                    contextData: {
                        contextId: ctxFields.contextId,
                        contextType: ctxFields.contextType,
                        appDefId: appDefinitionId,
                    },
                    resourceId: commentId,
                    resourceFqdn: COMMENTS_FQDN,
                })
                    .then((result) => result.type === 'SUCCESS'
                    ? { type: 'DOWNVOTE', voteId: result.response.voteId }
                    : { type: 'FAIL', error: result.error });
            }
            case 'NEUTRAL': {
                return votesService
                    .removeVote({
                    contextData: {
                        contextId: ctxFields.contextId,
                        contextType: ctxFields.contextType,
                        appDefId: appDefinitionId,
                    },
                    voteId: freshCurrentUserVote.voteId,
                })
                    .then((result) => result.type === 'SUCCESS'
                    ? { type: 'NEUTRAL' }
                    : { type: 'FAIL', error: result.error });
            }
            default:
                throw unreachable(freshCurrentUserVote.pendingState);
        }
    })();
    if (finalCurrentUserVoteState.type === 'FAIL') {
        return dispatch(voteOnCommentFailure({
            resourceId,
            commentId,
            error: finalCurrentUserVoteState.error,
            nextCurrentUserVoteState: freshCurrentUserVote,
        }));
    }
    return dispatch(voteOnCommentSuccess({
        resourceId,
        commentId,
        nextCurrentUserVoteState: finalCurrentUserVoteState,
        biInfo: {
            transition: {
                from: freshCommentState.votes.currentUserVote.type,
                to: finalCurrentUserVoteState.type,
            },
        },
    }));
};
const getCommentStateWithVotes = (state, resourceId, commentId) => {
    const resourceState = state.comments[resourceId];
    if (resourceState?.type !== 'READY') {
        return undefined;
    }
    const commentState = resourceState.commentsById[commentId];
    if (!commentState || !canHaveVotes(commentState)) {
        return;
    }
    return commentState;
};
export const isCommmentsVoteAction = isOfType([VOTE_ON_COMMENT_REQUEST, VOTE_ON_COMMENT_FAILURE, VOTE_ON_COMMENT_SUCCESS]);
//# sourceMappingURL=comments-vote-actions.js.map